import { TimePicker, TimePickerProps } from '@mui/x-date-pickers/TimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Controller, UseFormTrigger } from 'react-hook-form';
import { memo } from 'react';

/**
 * Interface for TimePickerController props
 * @template TDate The type of date used by the TimePicker
 * @extends Omit<TimePickerProps<TDate>, ''>
 */
interface TimePickerInterface<TDate> extends Omit<TimePickerProps<TDate>, ''> {
  /** React Hook Form control object */
  control: any;
  /** Name of the form field */
  name: string;
  /** Label for the TimePicker */
  label: string;
  /** Optional custom styles for the TimePicker */
  style?: React.CSSProperties;
  /** Optional additional onChange handler */
  addOnChange?: (params: any) => void;
  /** Optional object containing form errors */
  errors?: any;
  trigger?: UseFormTrigger<any>;
}

/**
 * TimePickerController component
 *
 * A controlled TimePicker component that integrates with React Hook Form.
 *
 * @template TDate The type of date used by the TimePicker
 * @param {TimePickerInterface<TDate>} props - The component props
 * @returns {React.ReactElement} The rendered TimePickerController component
 */
const TimePickerController = <TDate,>({
  control,
  name,
  label,
  style,
  addOnChange,
  errors,
  trigger,
  ...etc
}: TimePickerInterface<TDate>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <TimePicker
            {...field}
            onChange={(time) => {
              field.onChange(time);
              addOnChange && addOnChange(time);
            }}
            label={label}
            slotProps={{
              textField: {
                size: 'small',
              },
            }}
            sx={{
              width: '100%',
              '& .MuiInputAdornment-root button': {
                border: 0,
                color: '#161C248F',
                bgcolor: 'transparent',
              },
              ...style,
            }}
            {...etc}
            onClose={() => {
              etc?.onClose?.();
              if (trigger && errors?.[name]) {
                trigger(name);
              }
            }}
          />
        </LocalizationProvider>
      )}
    />
  );
};

export default memo(TimePickerController);
